import classNames from 'classnames';
import { Box, Typography } from '@mui/material';

import { useMaxWidthMobileLarge } from 'hooks/useMediaQueries';
import { RewindError, RewindErrorType } from 'utils/rewinds/playlistBlocks.types';
import { GRAPH_BAR_WIDTH_IN_MS, WaveGraphBar } from 'utils/rewinds/waveInsightData';
import type { RewindGraphGridParams } from '../hooks/useRewindGraphGridParams';

import styles from './RewindGraphContainer.module.scss';

const waveInsightsErrorMessage = (rewindErrorType: RewindErrorType, isShort: boolean) => {
  switch (rewindErrorType) {
    case RewindErrorType.GENERATING_INSIGHTS:
      if (isShort) return 'Processing…';
      return 'Insights processing…';
    case RewindErrorType.VISIBILITY_BAD_LOW_WAVES:
    case RewindErrorType.VISIBILITY_BAD:
      return 'Poor visibility';
    case RewindErrorType.VISIBILITY_GOOD_LOW_WAVES:
    case RewindErrorType.NO_WAVES_TRACKED:
      return 'No waves tracked';
    case RewindErrorType.NO_DATA:
      if (isShort) return 'No data';
      return 'No wave data available';
    default:
      if (isShort) return 'No data';
      return 'No wave data available';
  }
};

enum RoundMethod {
  UP = 'UP',
  DOWN = 'DOWN',
}

const convertDateToColumnNumber = ({
  date,
  bucketStartTimeInMs,
  roundingMethod,
}: {
  date: Date;
  bucketStartTimeInMs: number;
  roundingMethod: RoundMethod;
}) => {
  const columnLocation = (date.getTime() - bucketStartTimeInMs) / GRAPH_BAR_WIDTH_IN_MS;

  if (roundingMethod === RoundMethod.UP) {
    return Math.ceil(columnLocation);
  }
  return Math.floor(columnLocation);
};

const matchingBarHasWaves = (
  date: Date,
  waveGraphBars: WaveGraphBar[],
  bucketStartTimeInMs: number,
) => {
  const dateColNumber = convertDateToColumnNumber({
    date,
    bucketStartTimeInMs,
    roundingMethod: RoundMethod.DOWN,
  });

  return waveGraphBars.some((bar) => {
    if ('startTime' in bar && 'height' in bar) {
      if (bar.height === 0) return false;
      const barColNumber = convertDateToColumnNumber({
        date: new Date(bar.startTime),
        bucketStartTimeInMs,
        roundingMethod: RoundMethod.DOWN,
      });
      return dateColNumber === barColNumber;
    }
    return false;
  });
};

interface Props {
  bucketStartTimeInMs: number;
  waveGraphBars: WaveGraphBar[];
  graphGridParameters: RewindGraphGridParams;
  errors: RewindError[];
}

export const RewindGraphErrors: React.FC<Props> = ({
  bucketStartTimeInMs,
  waveGraphBars,
  graphGridParameters,
  errors,
}) => {
  const isMobileLarge = useMaxWidthMobileLarge();

  return (
    <>
      {errors.map((error: RewindError) => {
        const startCol = convertDateToColumnNumber({
          date: error.startDate,
          bucketStartTimeInMs,
          // we choose to round up or down dependent on whether the bar where the error starts has waves
          // - if the bar has waves, we start the error bar from the next 15 second block (round up)
          // - if the bar doesn't have waves, we start the error from the beginning of that bar (round down)
          // this ensures that the bars never overlap waves from previous rewinds, but that there also aren't
          // gaps between errors if errors appear one after the other
          roundingMethod: matchingBarHasWaves(error.startDate, waveGraphBars, bucketStartTimeInMs)
            ? RoundMethod.UP
            : RoundMethod.DOWN,
        });

        const endCol = convertDateToColumnNumber({
          date: error.endDate,
          bucketStartTimeInMs,
          roundingMethod: RoundMethod.DOWN,
        });

        if (startCol > endCol) return null;

        return (
          <Box
            key={`${error.type}-${error.startDate.getTime()}`}
            data-testid="wave-insights-graph-error"
            className={classNames(styles.waveBarGraphError)}
            style={{
              gridColumnStart: startCol + graphGridParameters.chartStartColumn,
              gridColumnEnd: endCol + graphGridParameters.chartStartColumn,
              height: graphGridParameters.errorHeight,
            }}
            gridRow={graphGridParameters.chartAreaRows}
          >
            <Typography className={styles.missingDataText} variant="caption2" noWrap>
              {waveInsightsErrorMessage(error.type, isMobileLarge)}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};
export default RewindGraphErrors;
